// src/components/friendship/FriendshipModulesPreview.jsx - Simple preview for homepage
import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Clock, Heart, BookOpen } from 'lucide-react';
import { useApp } from '../../context/AppContext';

const FriendshipModulesPreview = () => {
  const { setCurrentView } = useApp();

  // Featured modules for preview
  const previewModules = [
    {
      id: 1,
      title: 'Your Social Brain',
      subtitle: 'Understanding how your brain handles friendship',
      duration: '12 min',
      level: 'Foundation',
      image: '/images/friendship-01-social-brain.png',
    },
    {
      id: 2,
      title: 'Group Dynamics & Roles',
      subtitle: 'Decode the hidden patterns in friend groups',
      duration: '15 min',
      level: 'Foundation',
      image: '/images/friendship-02-roles.png',
    },
    {
      id: 6,
      title: 'Finding Your People',
      subtitle: 'Attract genuine, lasting friendships',
      duration: '22 min',
      level: 'Advanced',
      image: '/images/friendship-06-your-people.png',
    },
  ];

  const handleViewAllModules = () => {
    setCurrentView('modules');
  };

  const handleModuleClick = (moduleId) => {
    setCurrentView('modules');
  };

  const getLevelGradient = (level) => {
    const gradients = {
      Foundation: 'from-emerald-500 to-teal-500',
      Essential: 'from-blue-500 to-indigo-500',
      Advanced: 'from-purple-500 to-pink-500',
      Mastery: 'from-red-500 to-orange-500',
    };
    return gradients[level] || gradients.Foundation;
  };

  return (
    <section className="py-20 bg-gradient-to-br from-slate-800/30 to-purple-900/20">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <BookOpen className="w-6 h-6 text-purple-400" />
            <span className="text-purple-400 font-medium">Learning Modules</span>
          </div>

          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Master Real Friendship Skills
          </h2>

          <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            8 interactive modules packed with skills you'll actually use—from handling conflict to setting boundaries without the awkward.
            <span className="text-purple-300 font-semibold"> Real tools for real situations.</span>
          </p>
        </motion.div>

        {/* Preview Modules */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {previewModules.map((module, index) => (
            <motion.div
              key={module.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.02, y: -4 }}
              className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-2xl border border-slate-700/50 overflow-hidden hover:border-purple-500/50 transition-all duration-300 cursor-pointer hover:shadow-lg hover:shadow-purple-500/20"
              onClick={() => handleModuleClick(module.id)}
            >
              {/* Module Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={module.image}
                  alt={`${module.title} illustration`}
                  className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                />
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/30 to-transparent" />

                {/* Module number */}
                <div className="absolute top-3 left-3">
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${getLevelGradient(module.level)} shadow-lg flex items-center justify-center`}>
                    <span className="text-white text-sm font-bold">{module.id}</span>
                  </div>
                </div>

                {/* Level badge */}
                <div className="absolute top-3 right-3">
                  <div className={`px-2 py-1 rounded text-xs font-medium bg-gradient-to-r ${getLevelGradient(module.level)} text-white`}>
                    {module.level}
                  </div>
                </div>

                {/* Module info overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="text-lg font-bold text-white mb-1">
                    {module.title}
                  </h3>
                  <p className="text-sm text-purple-200">
                    {module.subtitle}
                  </p>
                </div>
              </div>

              {/* Module details */}
              <div className="p-6">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-4 text-slate-400">
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {module.duration}
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-purple-400 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <button
            onClick={handleViewAllModules}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-300 shadow-lg hover:shadow-purple-500/25 hover:scale-105"
          >
            <Heart className="w-5 h-5" />
            View All 8 Modules
            <ArrowRight className="w-5 h-5" />
          </button>

          <p className="text-slate-400 text-sm mt-4">
            Build genuine friendship skills that last a lifetime
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default FriendshipModulesPreview;